import { useCallback, useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { useExecuteAuth } from '../hooks/useExecuteAuth'
import { useModalApplication } from '../hooks/useModalApplication'
import { AuthResult } from '../models/applicationConnection'
import { ResultStep } from './ConnectionSteps/ResultStep'

export function useAppLoginManager() {
  const { t } = useTranslation('common')
  const { handleModal, updateModal, resetModal } = useModalApplication()
  const { handleExecuteAuth } = useExecuteAuth()

  const [authResult, setAuthResult] = useState<AuthResult>({
    success: false,
    loading: true,
  })
  const [authError, setAuthError] = useState<boolean>(false)
  const [friendlyMessage, setFriendlyMessage] = useState<string | null>(null)
  const [appData, setAppData] = useState<{
    applicationId: string
    applicationName: string
    userId: string
    accountUserId: string
    accountName?: string
  } | null>(null)

  const renderStep = () => {
    if (!appData) return null

    const stepInfo = { current: 1, total: 1 }

    return (
      <ResultStep
        appData={appData}
        authResult={authResult}
        stepInfo={stepInfo}
        namespace="app_login"
        authError={authError}
        friendlyMessage={friendlyMessage}
      />
    )
  }

  const handleNext = useCallback(async () => {
    if (authError) {
      setAuthError(false)
      setFriendlyMessage(null)
    }
    setAuthResult({ success: false, loading: false })
    setAppData(null)
    resetModal()
  }, [resetModal, authError])

  useEffect(() => {
    if (!appData) return

    updateModal({
      title: t('app_login.modal.title'),
      description: renderStep(),
      confirmText: t('app_login.buttons.close'),
      showButtons: !authResult.loading,
      onConfirm: handleNext,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authResult, appData, authError, t])

  const handleLoginApplication = useCallback(
    async (
      applicationId: string,
      userId: string,
      applicationName: string,
      accountUserId: string,
      accountName?: string,
    ) => {
      setAppData({
        applicationId,
        applicationName,
        userId,
        accountUserId,
        accountName,
      })
      setAuthResult({ success: false, loading: true })

      handleModal({
        title: t('app_login.modal.title'),
        description: renderStep(),
        showButtons: false,
        onConfirm: handleNext,
      })

      try {
        const response = await handleExecuteAuth(accountUserId)
        const loginUrl = `${response.application_url}?token=${response.token}`

        setAuthResult({ success: true, loading: false })

        setTimeout(() => {
          window.open(loginUrl, '_blank')
        }, 2000)
      } catch (error) {
        setAuthResult({ success: false, loading: false })
        setAuthError(true)
        if (error instanceof AxiosError) {
          const apiError = error.response?.data as ApiError
          setFriendlyMessage(apiError?.friendlyMessage || null)
        }
        queryClient.invalidateQueries({ queryKey: ['/user/get-user-info'] })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleModal, handleNext, handleExecuteAuth, resetModal, t],
  )

  return {
    handleLoginApplication,
  }
}
